import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DEFAULT_FIELD, DEFAULT_TABLE_CONFIG, DEFAULT_TEMPLATE_SETTINGS, MOCK_INVOICE_DATA } from './constants';
import api from '../../../services/api';

const useTemplateEditor = () => {
  // --- Template list state ---
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // --- Create form state ---
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateType, setNewTemplateType] = useState('invoice');
  const [creating, setCreating] = useState(false);

  // --- Editor state ---
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingFields, setEditingFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // --- Preview state ---
  const [previewLoading, setPreviewLoading] = useState(false);

  // --- Variables panel state ---
  const [showVariables, setShowVariables] = useState({});

  // --- Field expansion state ---
  const [expandedFields, setExpandedFields] = useState({});
  const [selectedFieldId, setSelectedFieldId] = useState(null);

  // --- Template management state ---
  const [editingTemplateName, setEditingTemplateName] = useState(null);
  const [duplicatingTemplate, setDuplicatingTemplate] = useState(null);

  // ========== API Functions ==========

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/custom-templates');
      const data = response.data;

      if (data.success) {
        setTemplates(data.data);
      } else {
        throw new Error(data.message || 'Błąd podczas ładowania szablonów');
      }
    } catch (err) {
      console.error('❌ Błąd ładowania szablonów:', err);
      setError('Nie udało się załadować szablonów: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createTemplate = useCallback(async () => {
    if (!newTemplateName.trim()) {
      setError('Nazwa szablonu jest wymagana');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const newTemplate = {
        name: newTemplateName.trim(),
        description: '',
        template_type: newTemplateType,
        config: {
          fields: [
            {
              id: "default-title",
              type: "text",
              x: 50,
              y: 50,
              width: 200,
              height: 20,
              content: "FAKTURA",
              style: {
                fontSize: 16,
                fontWeight: "bold",
                textAlign: "center",
                color: "#000000"
              }
            }
          ],
          settings: { ...DEFAULT_TEMPLATE_SETTINGS }
        }
      };

      const response = await api.post('/custom-templates', newTemplate);

      const data = response.data;

      if (data.success) {
        setSuccess(`Szablon "${newTemplateName}" został utworzony!`);
        setNewTemplateName('');
        setShowCreateForm(false);
        await loadTemplates();
      } else {
        throw new Error(data.message || 'Błąd podczas tworzenia szablonu');
      }
    } catch (err) {
      console.error('❌ Błąd tworzenia szablonu:', err);
      setError('Nie udało się utworzyć szablonu: ' + err.message);
    } finally {
      setCreating(false);
    }
  }, [newTemplateName, newTemplateType, loadTemplates]);

  const deleteTemplate = useCallback(async (templateId, templateName, isAnchored) => {
    if (isAnchored) {
      toast.error(`Szablon "${templateName}" jest zakotwiczony i nie może być usunięty. Najpierw odkotwicz szablon.`);
      return;
    }

    if (!window.confirm(`Czy na pewno chcesz usunąć szablon "${templateName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.delete(`/custom-templates/${templateId}`);

      const data = response.data;

      if (data.success) {
        setSuccess(`Szablon "${templateName}" został usunięty!`);
        await loadTemplates();
      } else {
        throw new Error(data.message || 'Błąd podczas usuwania szablonu');
      }
    } catch (err) {
      console.error('❌ Błąd usuwania szablonu:', err);
      setError('Nie udało się usunąć szablonu: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [loadTemplates]);

  const editTemplate = useCallback((template) => {
    const safeConfig = template?.config || {};
    const safeFields = Array.isArray(safeConfig.fields) ? safeConfig.fields : [];

    const templateWithDefaults = {
      ...template,
      config: {
        fields: safeFields,
        settings: {
          ...DEFAULT_TEMPLATE_SETTINGS,
          ...(safeConfig.settings || {})
        }
      }
    };

    setEditingTemplate(templateWithDefaults);

    const fieldsWithLines = safeFields.map(field => {
      const contentLines = field.contentLines || (field.content ? field.content.split('\n') : [""]);
      const lineStyles = field.lineStyles || contentLines.map(() => ({ bold: false }));

      return {
        ...field,
        contentLines,
        lineStyles
      };
    });
    setEditingFields(fieldsWithLines);
    setShowEditor(true);
  }, []);

  const renameTemplate = useCallback(async (templateId, newName) => {
    if (!newName.trim()) {
      setError('Nazwa szablonu nie może być pusta');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.put(`/custom-templates/${templateId}`, { name: newName.trim()
      });

      const data = response.data;

      if (data.success) {
        await loadTemplates();
        setSuccess(`Nazwa szablonu została zmieniona na "${newName}"`);
        setEditingTemplateName(null);
      } else {
        throw new Error(data.message || 'Błąd podczas zmiany nazwy szablonu');
      }
    } catch (err) {
      console.error('❌ Błąd zmiany nazwy szablonu:', err);
      setError('Nie udało się zmienić nazwy szablonu: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [loadTemplates]);

  const duplicateTemplateHandler = useCallback(async (template) => {
    try {
      setDuplicatingTemplate(template.id);
      setError(null);

      const response = await api.post(`/custom-templates/${template.id}/duplicate`, { name: `${template.name} - Kopia` });

      const data = response.data;

      if (data.success) {
        await loadTemplates();
        setSuccess(`Szablon został zduplikowany!`);
      } else {
        throw new Error(data.message || 'Błąd podczas duplikowania szablonu');
      }
    } catch (err) {
      console.error('❌ Błąd duplikowania szablonu:', err);
      setError('Nie udało się zduplikować szablonu: ' + err.message);
    } finally {
      setDuplicatingTemplate(null);
    }
  }, [loadTemplates]);

  const toggleTemplateAnchor = useCallback(async (templateId, currentAnchorState) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put(`/custom-templates/${templateId}`, { anchored: !currentAnchorState });

      const data = response.data;

      if (data.success) {
        await loadTemplates();
        setSuccess(`Szablon został ${!currentAnchorState ? 'zakotwiczony' : 'odkotwiczony'}`);
      } else {
        throw new Error(data.message || 'Błąd podczas zmiany kotwiczenia szablonu');
      }
    } catch (err) {
      console.error('❌ Błąd kotwiczenia szablonu:', err);
      setError('Nie udało się zmienić kotwiczenia szablonu: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [loadTemplates]);

  const saveTemplate = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);

      const updatedTemplate = {
        ...editingTemplate,
        config: {
          ...editingTemplate.config,
          fields: editingFields
        }
      };

      const response = await api.put(`/custom-templates/${editingTemplate.id}`, updatedTemplate);

      const data = response.data;

      if (data.success) {
        setSuccess(`Szablon "${editingTemplate.name}" został zapisany!`);
        setShowEditor(false);
        setEditingTemplate(null);
        await loadTemplates();
      } else {
        throw new Error(data.message || 'Błąd podczas zapisywania szablonu');
      }
    } catch (err) {
      console.error('❌ Błąd zapisywania szablonu:', err);
      setError('Nie udało się zapisać szablonu: ' + err.message);
    } finally {
      setSaving(false);
    }
  }, [editingTemplate, editingFields, loadTemplates]);

  const previewTemplateHandler = useCallback(async (template) => {
    try {
      setPreviewLoading(true);
      setError(null);

      const response = await api.post('/custom-template-preview', {
        template_config: template.config,
        invoice_data: MOCK_INVOICE_DATA
      }, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `podglad_szablonu_${template.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      try {
        window.open(url, '_blank');
      } catch (e) {
        // Popup zablokowany przez przeglądarkę - PDF został pobrany
      }

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      setSuccess(`Podgląd szablonu "${template.name}" został wygenerowany!`);
    } catch (err) {
      console.error('❌ Błąd podglądu szablonu:', err);
      setError('Nie udało się wygenerować podglądu: ' + err.message);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // ========== Field Management Functions ==========

  const addField = useCallback(() => {
    const newField = {
      ...DEFAULT_FIELD,
      id: `field-${Date.now()}`
    };
    setEditingFields(prev => [...prev, newField]);
  }, []);

  const updateField = useCallback((fieldId, updates) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id === fieldId) {
        const updatedField = { ...field, ...updates };

        if (updates.contentLines) {
          updatedField.content = updates.contentLines.join('\n');
        }

        if (updates.type === 'table' && !updatedField.tableConfig) {
          updatedField.tableConfig = { ...DEFAULT_TABLE_CONFIG };
        }

        return updatedField;
      }
      return field;
    }));
  }, []);

  const removeField = useCallback((fieldId) => {
    setEditingFields(prev => prev.filter(field => field.id !== fieldId));
  }, []);

  const addContentLine = useCallback((fieldId) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;
      const currentLines = field.contentLines || [field.content || ""];
      const newLines = [...currentLines, "Nowa linia"];
      const currentLineStyles = field.lineStyles || [];
      const newLineStyles = [...currentLineStyles, { bold: false }];
      const updatedField = {
        ...field,
        contentLines: newLines,
        lineStyles: newLineStyles,
        content: newLines.join('\n')
      };
      return updatedField;
    }));
  }, []);

  const updateContentLine = useCallback((fieldId, lineIndex, value) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;
      const currentLines = field.contentLines || [field.content || ""];
      const newLines = [...currentLines];
      newLines[lineIndex] = value;
      return {
        ...field,
        contentLines: newLines,
        content: newLines.join('\n')
      };
    }));
  }, []);

  const removeContentLine = useCallback((fieldId, lineIndex) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;
      if (!field.contentLines || field.contentLines.length <= 1) return field;
      const newLines = field.contentLines.filter((_, index) => index !== lineIndex);
      const newLineStyles = field.lineStyles ? field.lineStyles.filter((_, index) => index !== lineIndex) : [];
      return {
        ...field,
        contentLines: newLines,
        lineStyles: newLineStyles,
        content: newLines.join('\n')
      };
    }));
  }, []);

  const toggleLineBold = useCallback((fieldId, lineIndex) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;

      const currentLineStyles = [...(field.lineStyles || [])];
      const linesCount = field.contentLines ? field.contentLines.length : 1;
      while (currentLineStyles.length < linesCount) {
        currentLineStyles.push({ bold: false });
      }

      const wasBold = currentLineStyles[lineIndex]?.bold || false;
      currentLineStyles[lineIndex] = {
        ...currentLineStyles[lineIndex],
        bold: !wasBold
      };

      return { ...field, lineStyles: currentLineStyles };
    }));
  }, []);

  // ========== Global Settings Functions ==========

  const updateGlobalSetting = useCallback((settingName, value) => {
    if (editingTemplate) {
      const currentConfig = editingTemplate.config || {};
      const currentSettings = currentConfig.settings || {};

      const updatedTemplate = {
        ...editingTemplate,
        config: {
          ...currentConfig,
          settings: {
            ...currentSettings,
            [settingName]: value
          }
        }
      };
      setEditingTemplate(updatedTemplate);
    }
  }, [editingTemplate]);

  const resetAllFontsToGlobal = useCallback(() => {
    const confirmReset = window.confirm(
      'Czy na pewno chcesz zresetować wszystkie indywidualne ustawienia czcionek?\n\n' +
      'Wszystkie pola będą używać globalnych ustawień czcionki określonych powyżej.\n\n' +
      'Ta operacja nie może być cofnięta.'
    );

    if (confirmReset) {
      const resetFields = editingFields.map(field => {
        const newStyle = { ...field.style };
        delete newStyle.fontSize;
        delete newStyle.fontFamily;
        delete newStyle.fontWeight;

        return { ...field, style: newStyle };
      });

      setEditingFields(resetFields);
    }
  }, [editingFields]);

  // ========== Variable Insertion Functions ==========

  const insertVariable = useCallback((fieldId, variableName) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;

      if (field.contentLines && field.contentLines.length > 0) {
        const lastIndex = field.contentLines.length - 1;
        const currentLine = field.contentLines[lastIndex] || '';
        const newLines = [...field.contentLines];
        newLines[lastIndex] = currentLine + variableName;
        return { ...field, contentLines: newLines, content: newLines.join('\n') };
      } else {
        const currentContent = field.content || '';
        return { ...field, content: currentContent + variableName };
      }
    }));
  }, []);

  const insertVariableNewLine = useCallback((fieldId, variableName) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;

      if (field.contentLines && field.contentLines.length > 0) {
        const newLines = [...field.contentLines, variableName];
        return { ...field, contentLines: newLines, content: newLines.join('\n') };
      } else {
        const currentContent = field.content || '';
        const newContent = currentContent + (currentContent ? '\n' : '') + variableName;
        return { ...field, content: newContent };
      }
    }));
  }, []);

  const toggleVariablesPanel = useCallback((fieldId) => {
    setShowVariables(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  }, []);

  // ========== Field Expansion Functions ==========

  const toggleFieldExpansion = useCallback((fieldId) => {
    setExpandedFields(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  }, []);

  const selectField = useCallback((fieldId) => {
    setSelectedFieldId(fieldId);
    setExpandedFields(prev => ({
      ...prev,
      [fieldId]: true
    }));
  }, []);

  const collapseAllFields = useCallback(() => {
    setExpandedFields({});
    setSelectedFieldId(null);
  }, []);

  const expandAllFields = useCallback(() => {
    const expanded = {};
    editingFields.forEach(field => {
      expanded[field.id] = true;
    });
    setExpandedFields(expanded);
  }, [editingFields]);

  // ========== Field Reordering Functions ==========

  const moveFieldUp = useCallback((fieldId) => {
    setEditingFields(prev => {
      const currentIndex = prev.findIndex(f => f.id === fieldId);
      if (currentIndex <= 0) return prev;
      const newFields = [...prev];
      [newFields[currentIndex - 1], newFields[currentIndex]] = [newFields[currentIndex], newFields[currentIndex - 1]];
      return newFields;
    });
  }, []);

  const moveFieldDown = useCallback((fieldId) => {
    setEditingFields(prev => {
      const currentIndex = prev.findIndex(f => f.id === fieldId);
      if (currentIndex === -1 || currentIndex >= prev.length - 1) return prev;
      const newFields = [...prev];
      [newFields[currentIndex], newFields[currentIndex + 1]] = [newFields[currentIndex + 1], newFields[currentIndex]];
      return newFields;
    });
  }, []);

  const moveFieldToPosition = useCallback((fieldId, newPosition) => {
    setEditingFields(prev => {
      const currentIndex = prev.findIndex(f => f.id === fieldId);
      if (currentIndex === -1 || newPosition < 0 || newPosition >= prev.length) return prev;
      const newFields = [...prev];
      const [movedField] = newFields.splice(currentIndex, 1);
      newFields.splice(newPosition, 0, movedField);
      return newFields;
    });
  }, []);

  // ========== Column Reordering Functions ==========

  const moveColumnUp = useCallback((fieldId, columnIndex) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId || !field.tableConfig?.columns || columnIndex <= 0) return field;
      const newColumns = [...field.tableConfig.columns];
      [newColumns[columnIndex - 1], newColumns[columnIndex]] = [newColumns[columnIndex], newColumns[columnIndex - 1]];
      return { ...field, tableConfig: { ...field.tableConfig, columns: newColumns } };
    }));
  }, []);

  const moveColumnDown = useCallback((fieldId, columnIndex) => {
    setEditingFields(prev => prev.map(field => {
      if (field.id !== fieldId || !field.tableConfig?.columns || columnIndex >= field.tableConfig.columns.length - 1) return field;
      const newColumns = [...field.tableConfig.columns];
      [newColumns[columnIndex], newColumns[columnIndex + 1]] = [newColumns[columnIndex + 1], newColumns[columnIndex]];
      return { ...field, tableConfig: { ...field.tableConfig, columns: newColumns } };
    }));
  }, []);

  // ========== Editor Close ==========

  const closeEditor = useCallback(() => {
    setShowEditor(false);
    setEditingTemplate(null);
    setEditingFields([]);
  }, []);

  const closeCreateForm = useCallback(() => {
    setShowCreateForm(false);
    setNewTemplateName('');
    setNewTemplateType('invoice');
    setError(null);
  }, []);

  // ========== Effects ==========

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  return {
    // State
    templates,
    loading,
    error,
    success,
    showCreateForm,
    newTemplateName,
    newTemplateType,
    creating,
    editingTemplate,
    showEditor,
    editingFields,
    saving,
    previewLoading,
    showVariables,
    expandedFields,
    selectedFieldId,
    editingTemplateName,
    duplicatingTemplate,

    // State setters
    setError,
    setSuccess,
    setShowCreateForm,
    setNewTemplateName,
    setNewTemplateType,
    setEditingTemplateName,

    // API functions
    loadTemplates,
    createTemplate,
    deleteTemplate,
    editTemplate,
    renameTemplate,
    duplicateTemplate: duplicateTemplateHandler,
    toggleTemplateAnchor,
    saveTemplate,
    previewTemplateHandler,

    // Field management
    addField,
    updateField,
    removeField,
    addContentLine,
    updateContentLine,
    removeContentLine,
    toggleLineBold,

    // Global settings
    updateGlobalSetting,
    resetAllFontsToGlobal,

    // Variables
    insertVariable,
    insertVariableNewLine,
    toggleVariablesPanel,

    // Field expansion
    toggleFieldExpansion,
    selectField,
    collapseAllFields,
    expandAllFields,

    // Field reordering
    moveFieldUp,
    moveFieldDown,
    moveFieldToPosition,

    // Column reordering
    moveColumnUp,
    moveColumnDown,

    // Editor control
    closeEditor,
    closeCreateForm,
  };
};

export default useTemplateEditor;
